import React, { useState, useEffect } from 'react';
import { getAuthHeader } from '../../utils';
import customAxios from '../../customHook/customAxios';
import { v3_get_expense_exchange_rates } from '../../actions/v3/expenses';
import { defaultExpenseCurrencies, normalizeExpenseCurrencyCode, toExpenseCurrencyOption } from '../../expenseCurrency';

const defaultCurrencyCode = 'CNY';
const defaultCurrencyOptions = defaultExpenseCurrencies.map(toExpenseCurrencyOption);

const sanitizeCurrencyCode = (value) => normalizeExpenseCurrencyCode(value);
const sanitizeNumber = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const calculateUsdEquivalent = (amount, currency, rates = {}) => {
  const amountNumber = sanitizeNumber(amount);
  const currencyCode = sanitizeCurrencyCode(currency);
  const rate = Number(rates?.[currencyCode]);

  if (!currencyCode || amountNumber <= 0 || !Number.isFinite(rate) || rate <= 0) {
    return '';
  }

  return (amountNumber / rate).toFixed(2);
};

const normalizeCurrencyOptions = (supportedCurrencies = []) => {
  if (!Array.isArray(supportedCurrencies) || !supportedCurrencies.length) {
    return defaultCurrencyOptions;
  }

  return supportedCurrencies
    .map((currency) => {
      const code = sanitizeCurrencyCode(currency?.code);
      if (!code) return null;

      return toExpenseCurrencyOption({
        code,
        symbol: currency?.symbol || code,
        label: currency?.label || currency?.name || code,
      });
    })
    .filter(Boolean);
};

const buildExpenseItemsFromData = (data = {}) => {
  if (Array.isArray(data?.expense_items)) {
    return data.expense_items.map((item) => ({
      expenseTypeId: item.EXPENSE_TYPE_ID,
      currency: sanitizeCurrencyCode(item.CURRENCY) || defaultCurrencyCode,
      description: item.DESCRIPTION,
      amount: item.AMOUNT,
      usdEquiv: item.USD_EQUIV,
      accountCode: item.ACCOUNT_CODE || '',
      previousDocumentProof: item.PROOF_DOCUMENT ? item.PROOF_DOCUMENT : null,
      documentProof: null,
    }));
  }

  if (Array.isArray(data?.expenseItems)) {
    return data.expenseItems.map((item) => ({
      ...item,
      currency: sanitizeCurrencyCode(item.currency) || defaultCurrencyCode,
      accountCode: item.accountCode || '',
      previousDocumentProof: item.previousDocumentProof || null,
      documentProof: null,
    }));
  }

  return [{
    expenseTypeId: '',
    description: '',
    currency: defaultCurrencyCode,
    amount: '',
    usdEquiv: '',
    accountCode: '',
    documentProof: null,
  }];
};

const buildInitialState = (data = {}) => ({
  projectLocationId: {
    name: 'projectLocationId',
    value: data?.SITE_LOCATION || data?.PROJECT_LOCATION_ID || '',
    isRequired: true,
    error: '',
    placeholder: 'Enter project location ID',
  },
  expenseFromDate: {
    name: 'expenseFromDate',
    value: data?.EXPENSE_FROM_DATE || data?.EXPENSE_DATE || '',
    isRequired: true,
    error: '',
    placeholder: 'Select from date',
  },
  expenseToDate: {
    name: 'expenseToDate',
    value: data?.EXPENSE_TO_DATE || data?.EXPENSE_DATE || '',
    isRequired: true,
    error: '',
    placeholder: 'Select to date',
  },
  purpose: {
    name: 'purpose',
    value: data?.PURPOSE || '',
    isRequired: true,
    error: '',
    placeholder: 'Enter purpose of expense',
  },
  totalAmount: {
    name: 'totalAmount',
    value: data?.TOTAL_AMOUNT || '',
    isRequired: true,
    error: '',
    placeholder: 'Enter total amount',
  },
  totalUsdEquiv: {
    name: 'totalUsdEquiv',
    value: data?.TOTAL_USD_EQUIV || '',
    isRequired: true,
    error: '',
    placeholder: 'Enter total USD equivalent',
  },
  expenseItems: buildExpenseItemsFromData(data),
});

const formatFxDateTime = (value) => {
  if (!value) return '';
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return '';
  return parsedDate.toLocaleString();
};

const hasExchangeRate = (currencyCode, exchangeRates = {}) => {
  const code = sanitizeCurrencyCode(currencyCode);
  const rate = Number(exchangeRates?.[code]);
  return Number.isFinite(rate) && rate > 0;
};

// Utility function for validation
export const validateFunctionalForm = (state) => {
  let errors = {};

  const fieldsToValidate = [
    'projectLocationId',
    'expenseFromDate',
    'expenseToDate',
    'purpose',
    'totalAmount',
    'totalUsdEquiv',
  ];

  fieldsToValidate.forEach((field) => {
    if (state[field].isRequired && !state[field].value) {
      errors[field] = `${state[field].name} is required`;
    }
  });

  if (Array.isArray(state.expenseItems)) {
    state.expenseItems.forEach((item, index) => {
      const itemErrors = {};

      if (!item.expenseTypeId) {
        itemErrors.expenseTypeId = 'Expense type is required';
      }
      if (!item.currency) {
        itemErrors.currency = 'Currency is required';
      }

      if (!item.description || item.description.trim() === '') {
        itemErrors.description = 'Description is required';
      }

      if (!item.amount || item.amount <= 0) {
        itemErrors.amount = 'Amount must be greater than 0';
      }

      if (!item.usdEquiv || item.usdEquiv <= 0) {
        itemErrors.usdEquiv = 'USD Equivalent must be greater than 0';
      }

      if (Object.keys(itemErrors).length > 0) {
        if (!errors.expenseItems) errors.expenseItems = [];
        errors.expenseItems[index] = itemErrors;
      }
    });
  }

  return errors;
};

const ExpenseEntryForm = ({ data, onSubmit, expenseTypes = [], siteLocations = [] }) => {
  const [state, setState] = useState(buildInitialState(data));
  const [errors, setErrors] = useState({});
  const [currencyType, setCurrencyType] = useState(defaultCurrencyOptions);
  const [exchangeRates, setExchangeRates] = useState({ USD: 1 });
  const [fxStatus, setFxStatus] = useState({
    loading: true,
    error: '',
    provider: '',
    asOf: '',
  });

  useEffect(() => {
    let isMounted = true;

    const loadExchangeRates = async () => {
      try {
        const response = await v3_get_expense_exchange_rates();
        if (!isMounted) return;

        if (!response?.status || !response?.result?.rates) {
          throw new Error(response?.error || 'Failed to fetch exchange rates.');
        }

        const rawRates = response.result.rates || {};
        const normalizedRates = Object.keys(rawRates).reduce((acc, key) => {
          const rate = Number(rawRates[key]);
          if (Number.isFinite(rate) && rate > 0) {
            acc[sanitizeCurrencyCode(key)] = rate;
          }
          return acc;
        }, { USD: 1 });

        setExchangeRates(normalizedRates);
        setCurrencyType(normalizeCurrencyOptions(response.result.supportedCurrencies));
        setFxStatus({
          loading: false,
          error: '',
          provider: response.result.provider || '',
          asOf: response.result.asOf || '',
        });
      } catch (error) {
        if (!isMounted) return;

        setFxStatus({
          loading: false,
          error: 'Live exchange rates are unavailable right now. Enter USD equivalent manually.',
          provider: '',
          asOf: '',
        });
        setCurrencyType(defaultCurrencyOptions);
      }
    };

    loadExchangeRates();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!Array.isArray(state.expenseItems)) return;

    const totalAmount = state.expenseItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const totalUsdEquiv = state.expenseItems.reduce((sum, item) => sum + Number(item.usdEquiv || 0), 0);

    setState((prev) => ({
      ...prev,
      totalAmount: {
        ...prev.totalAmount,
        value: totalAmount.toFixed(2),
      },
      totalUsdEquiv: {
        ...prev.totalUsdEquiv,
        value: totalUsdEquiv.toFixed(2),
      },
    }));
  }, [state.expenseItems]);

  useEffect(() => {
    if (!Array.isArray(state.expenseItems) || !Object.keys(exchangeRates).length) return;

    setState((prev) => {
      const convertedItems = prev.expenseItems.map((item) => {
        const computedUsd = calculateUsdEquivalent(item.amount, item.currency, exchangeRates);
        if (computedUsd === '') return item;
        if (String(item.usdEquiv) === String(computedUsd)) return item;
        return {
          ...item,
          usdEquiv: computedUsd,
        };
      });

      const changed = convertedItems.some((item, index) => item !== prev.expenseItems[index]);
      if (!changed) return prev;

      return {
        ...prev,
        expenseItems: convertedItems,
      };
    });
  }, [exchangeRates]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value,
        error: prev[name].isRequired && !value ? 'This field is required' : '',
      },
    }));
  };

  const handleExpenseItemChange = (index, e) => {
    const { name, value, files } = e.target;
    const cleanName = name.split('-')[0];

    setState((prev) => {
      const newItems = [...prev.expenseItems];
      const nextValue = cleanName === 'documentProof' ? files?.[0] : value;
      const nextItem = {
        ...newItems[index],
        [cleanName]: nextValue,
      };

      if (cleanName === 'currency') {
        nextItem.currency = sanitizeCurrencyCode(nextValue);
      }

      if (cleanName === 'amount' || cleanName === 'currency') {
        const computedUsd = calculateUsdEquivalent(nextItem.amount, nextItem.currency, exchangeRates);
        if (computedUsd !== '') {
          nextItem.usdEquiv = computedUsd;
        } else if (cleanName === 'currency') {
          nextItem.usdEquiv = '';
        }
      }

      newItems[index] = nextItem;

      return {
        ...prev,
        expenseItems: newItems,
      };
    });
  };

  const handleAddExpenseItem = () => {
    const defaultCurrency =
      currencyType.find((item) => item.value === defaultCurrencyCode)?.value ||
      currencyType?.[0]?.value ||
      'USD';

    setState((prev) => ({
      ...prev,
      expenseItems: [
        ...prev.expenseItems,
        { expenseTypeId: '', description: '', currency: defaultCurrency, amount: '', usdEquiv: '', accountCode: '', documentProof: null },
      ],
    }));
  };

  const handleRemoveExpenseItem = (index) => {
    const newExpenseItems = [...state.expenseItems];
    newExpenseItems.splice(index, 1);
    setState((prev) => ({
      ...prev,
      expenseItems: newExpenseItems,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validateFunctionalForm(state);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      const formData = new FormData();

      Object.keys(state).forEach((key) => {
        if (key !== 'expenseItems') {
          formData.append(key, state[key].value);
        }
      });

      state.expenseItems.forEach((item, index) => {
        formData.append(`expenseItems[${index}][expenseTypeId]`, item.expenseTypeId);
        formData.append(`expenseItems[${index}][description]`, item.description);
        formData.append(`expenseItems[${index}][currency]`, sanitizeCurrencyCode(item.currency));
        formData.append(`expenseItems[${index}][amount]`, item.amount);
        formData.append(`expenseItems[${index}][usdEquiv]`, item.usdEquiv);
        formData.append(`expenseItems[${index}][accountCode]`, item.accountCode || '');
        if (item.previousDocumentProof) {
          formData.append(`expenseItems[${index}][previousDocumentProof]`, item.previousDocumentProof);
        }
        if (item.documentProof) {
          formData.append(`expenseItems[${index}][documentProof]`, item.documentProof);
        }
      });

      formData.append('USER_ID', 1);
      onSubmit(formData);
    }
  };

  const handleReset = () => {
    setErrors({});
    setState(buildInitialState(data));
  };

  const downloadProofFile = async (fileName) => {
    try {
      const response = await customAxios.get(
        `${process.env.REACT_APP_BASE_URL}/v2/expenseProof/${fileName}`,
        {
          ...getAuthHeader(),
          responseType: 'blob',
        }
      );

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", err);
      alert("Unable to download the file.");
    }
  };

  const fxDateTime = formatFxDateTime(fxStatus.asOf);

  return (
    <form onSubmit={handleSubmit}>
      {/* <h4 className="card-title mb-4">{title}</h4> */}
      <div className="row">
        <div className="col-md-6 mb-3">
          <label>Site Location <span className='text-danger'>*</span></label>
          <select
            name={state.projectLocationId.name}
            value={state.projectLocationId.value}
            onChange={handleChange}
            className={`form-control ${errors.projectLocationId ? 'is-invalid' : ''}`}
          >
            <option value="">Select Site Location</option>
            {siteLocations.map((type) => (
              <option key={type.ID} value={type.ID}>
                {type.LOCATION_NAME}
              </option>
            ))}
          </select>
          {errors.projectLocationId && (
            <div className="invalid-feedback">Site Location is required</div>
          )}
        </div>

        {[{ label: 'From Date', name: 'expenseFromDate', type: 'date' },
        { label: 'To Date', name: 'expenseToDate', type: 'date' },
        { label: 'Purpose', name: 'purpose' },
        { label: 'Total USD Equivalent', name: 'totalUsdEquiv', type: 'number' },
        ].map(({ label, name, type = 'text' }) => (
          <div className="col-md-6 mb-3" key={name}>
            <label>{label} {state[name].isRequired && <span className='text-danger'> *</span>}</label>
            <input
              type={type}
              name={state[name].name}
              value={state[name].value}
              onChange={handleChange}
              placeholder={state[name].placeholder}
              readOnly={name === 'totalAmount' || name === 'totalUsdEquiv'}
              className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
            />
            {errors[name] && <div className="invalid-feedback">{errors[name]}</div>}
          </div>
        ))}

        <div className="col-md-12 mb-2">
          {fxStatus.loading && (
            <small className="text-muted">Loading live exchange rates...</small>
          )}
          {!fxStatus.loading && !fxStatus.error && (
            <small className="text-success">
              Live exchange rates linked ({fxStatus.provider || 'provider'})
              {fxDateTime ? ` | Updated: ${fxDateTime}` : ''}
            </small>
          )}
          {!fxStatus.loading && fxStatus.error && (
            <small className="text-warning">{fxStatus.error}</small>
          )}
        </div>

        <div className="col-md-12 mb-3">
          <label>Expense Items <span className='text-danger'>*</span></label>
          {Array.isArray(state.expenseItems) &&
            state.expenseItems.map((item, index) => {
              const itemErrors = errors.expenseItems?.[index] || {};
              const usdAutoCalculated = hasExchangeRate(item.currency, exchangeRates);

              return (
                <div key={index} className="row mb-3">
                  <div className="col-md-2">
                    <select
                      name={`expenseTypeId-${index}`}
                      value={item.expenseTypeId}
                      onChange={(e) => handleExpenseItemChange(index, e)}
                      className={`form-select ${itemErrors.expenseTypeId ? 'is-invalid' : ''}`}
                    >
                      <option value="">Select Expense Type</option>
                      {expenseTypes.map((type) => (
                        <option key={type.ID} value={type.ID}>
                          {type.EXPENSE_TYPE_NAME}
                        </option>
                      ))}
                    </select>
                    {itemErrors.expenseTypeId && (
                      <div className="invalid-feedback">{itemErrors.expenseTypeId}</div>
                    )}
                  </div>

                  <div className="col-md-2">
                    <input
                      type="text"
                      name={`description-${index}`}
                      value={item.description}
                      onChange={(e) => handleExpenseItemChange(index, e)}
                      placeholder="Description"
                      className={`form-control ${itemErrors.description ? 'is-invalid' : ''}`}
                    />
                    {itemErrors.description && (
                      <div className="invalid-feedback">{itemErrors.description}</div>
                    )}
                  </div>
                  <div className="col-md-2">
                    <input
                      type="text"
                      name={`accountCode-${index}`}
                      value={item.accountCode}
                      onChange={(e) => handleExpenseItemChange(index, e)}
                      placeholder="Account Code"
                      className={`form-control ${itemErrors.accountCode ? 'is-invalid' : ''}`}
                    />
                    {itemErrors.accountCode && (
                      <div className="invalid-feedback">{itemErrors.accountCode}</div>
                    )}
                  </div>
                  <div className="col-md-2">
                    <select
                      name={`currency-${index}`}
                      value={item.currency}
                      onChange={(e) => handleExpenseItemChange(index, e)}
                      className={`form-select ${itemErrors.currency ? 'is-invalid' : ''}`}
                    >
                      <option value="">Select Currency</option>
                      {currencyType.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {itemErrors.currency && (
                      <div className="invalid-feedback">{itemErrors.currency}</div>
                    )}
                  </div>
                  <div className="col-md-2">
                    <input
                      type="number"
                      name={`amount-${index}`}
                      value={item.amount}
                      onChange={(e) => handleExpenseItemChange(index, e)}
                      placeholder="Amount"
                      className={`form-control ${itemErrors.amount ? 'is-invalid' : ''}`}
                    />
                    {itemErrors.amount && (
                      <div className="invalid-feedback">{itemErrors.amount}</div>
                    )}
                  </div>
                  <div className="col-md-2">
                    <input
                      type="number"
                      name={`usdEquiv-${index}`}
                      value={item.usdEquiv}
                      onChange={(e) => handleExpenseItemChange(index, e)}
                      placeholder="USD Equivalent"
                      readOnly={usdAutoCalculated}
                      className={`form-control ${itemErrors.usdEquiv ? 'is-invalid' : ''}`}
                    />
                    {itemErrors.usdEquiv && (
                      <div className="invalid-feedback">{itemErrors.usdEquiv}</div>
                    )}
                  </div>
                  <div className="col-md-3 mt-3">
                    {item.previousDocumentProof ? (
                      <div className="d-flex align-items-center gap-2">
                        <a
                          type='button text-primary'
                          onClick={() => downloadProofFile(item.previousDocumentProof)}
                          className="btn btn-link"
                        >
                          Download Proof
                        </a>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => {
                            const newItems = [...state.expenseItems];
                            newItems[index].previousDocumentProof = null;
                            setState((prev) => ({
                              ...prev,
                              expenseItems: newItems,
                            }));
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <input
                        type="file"
                        name={`documentProof-${index}`}
                        onChange={(e) => handleExpenseItemChange(index, e)}
                        className="form-control"
                      />
                    )}

                  </div>
                  <div className="col-md-1 mt-3">
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => handleRemoveExpenseItem(index)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          <span className='btn ms-1 text-danger' onClick={handleAddExpenseItem}>
            +  Add More Expense Item
          </span>
        </div>
      </div>

      <div className="mt-3">
        <button type="submit" className="btn btn-success me-2">
          Submit
        </button>
        <button type="button" className="btn btn-danger" onClick={handleReset}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export default ExpenseEntryForm;
